// React
import { Fragment, useContext } from "react"

// Components
import {
  BaseTable,
  Button,
  Card,
  Checkbox,
  ErrorText,
  HeaderMenu,
  Input,
  Loading,
  Select,
  TableHeader,
  TableSection,
  Textarea
} from "components"
import { Date } from "../../components"
import { Code } from "./Code"
import { LinkBill } from "./LinkBill"
import { TableRow } from "./TableRow"

// Contexts
import { DataContext, ListContext, ParamsContext } from "../contexts"

// Form
import { ErrorMessage } from "@hookform/error-message"
import { Controller, useFormContext, useWatch } from "react-hook-form"

// Icons
import { IoSendSharp } from "react-icons/io5"

// Third-Party Libraries
import toast from "react-hot-toast"

// Types
import type { SelectOptionType } from "types"

// Utils
import { convertNumber } from "utils"
import { type FormType, getSubtotalItem } from "../utils"
import { useApprovalStore } from "../../utils"

export function InputSection(params: {
  actionbutton?: JSX.Element
  isEdit?:boolean
}): JSX.Element {
  // Hooks
  const { data } = useContext(DataContext)
  const { isDisabled } = useContext(ParamsContext)
  const { isEdit, isView } = useApprovalStore()

  // Vars
  const defaultLocation: SelectOptionType[] = [
    {
      label: data.location_name,
      value: data.location_id
    }
  ]
  const defaultSupplier: SelectOptionType[] = [
    {
      label: data.vendor_name,
      value: data.vendor_id
    }
  ]

  // Form
  const { control, formState } = useFormContext<FormType>()

  return (
    <section className="container my-5 flex flex-col gap-5">
      <HeaderMenu 
        title={!isEdit && !isView ? `DATA ENTRY | REJECT ITEM` : `REJECTED ITEM`}
         action={isEdit ? "edit" : (isView ? "view" : "create")}
        >

        <Code />
      </HeaderMenu>
      
      <section className="grid lg:grid-cols-3 gap-x-6 gap-y-3">
        <section className="w-full">
          <Select
            isDisabled
            label="SUPPLIER"
            value={defaultSupplier[0]}
            options={defaultSupplier}
          />
        </section>

        <section>
          <div className="normal-case flex justify-evenly items-center pt-10">
            <div className="flex flex-col gap-2">
              <span>TRADING TERM </span>
            </div>

            <div className="px-2 flex flex-col gap-2">
              <IoSendSharp />
            </div>

            <div className="flex flex-col gap-2">
              <span>Net {data.payment_term} days after OEM</span>
            </div>
          </div>
        </section>

        <section className="lg:justify-self-end self-end mb-3">
          <Checkbox disabled value={data.tax_inclusive} label="">
            TAX-INCLUSIVE
          </Checkbox>
        </section>
      </section>

      <Card>
        <Card.Body className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
          <section className="flex flex-col gap-3">
            <section>
              <Select
                isDisabled
                label="DELIVER TO"
                value={defaultLocation[0]}
                options={defaultLocation}
              />

              <Textarea disabled value={data.address} />
            </section>

            <Controller
              name="memo"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label="MEMO"
                  error={fieldState.error?.message}
                  disabled={isDisabled || isView}
                />
              )}
            />

            <LinkBill />
          </section>

          <section className="flex flex-col gap-3">
            <Date name="transaction_date" disabled={isDisabled || isView} />
            {!params.isEdit && <Input disabled label="DELIVERY" value={`#${data.delivery_no}`} />}

            <Controller
              name="referensi"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label="REFERENCE"
                  error={fieldState.error?.message}
                  disabled={isDisabled || isView}
                />
              )}
            />
          </section>
        </Card.Body>
      </Card>

      <section>
        <BaseTable>
          <TableHeader>
            <tr>
              <th>BACK DELIVERED</th>
              <th>QTY REJECTED</th>
              <th>ITEM CODE</th>
              <th>DESCRIPTION</th>
              <th>UNIT</th>
              <th>PRICE</th>
              <th>DISC(%)</th>
              <th>TOTAL</th>
              <th>TAX</th>
              <th>ACTION</th>
            </tr>
          </TableHeader>

          <tbody>
            <RejectDetail />
          </tbody>
        </BaseTable>

        <ErrorMessage
          name="prorejectetail"
          errors={formState.errors}
          render={({ message }) => <ErrorText text={message} />}
        />
      </section>

      <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
        <Approval />
        <Acc />
      </section>
      
      {!params.isEdit && (
        <section>
          <section className="min-w-[126px]">
            <Button
              size="sm"
              color="ghost"
              className="no-animation w-fit bg-[#3c3c3c] hover:bg-primary hover:text-black whitespace-nowrap bg-primary text-black">
              DETAIL BILL
            </Button>
          </section>

          <BaseTable>
            <TableHeader>
              <tr>
                <th></th>
                <th>DELIVERED</th>
                <th>QTY REJECTED</th>
                <th>BACK DELIVERED</th>
                <th>ITEM CODE</th>
                <th>DESCRIPTION</th>
                <th>UNIT</th>
                <th>PRICE</th>
                <th>DISC(%)</th>
                <th>TOTAL</th>
                <th>TAX</th>
              </tr>
            </TableHeader>

            <tbody>
              <DetailBill />
            </tbody>
          </BaseTable>
        </section>
      )}
      {params.actionbutton}
    </section>
  )
}

function Acc(): JSX.Element {
  // Form
  const { control } = useFormContext<FormType>()
  const prorejectdetail = useWatch({
    control,
    name: "prorejectdetail"
  })

  // Vars
  const subtotal = prorejectdetail.reduce((acc, item) => {
    return acc + getSubtotalItem(item.discount, item.price_reject, item.quantity_reject)
  }, 0)
  const tax = prorejectdetail.reduce((acc, item) => {
    return acc + (getSubtotalItem(item.discount, item.price_reject, item.quantity_reject) * item.tax_rate / 100)
  }, 0)
  const total = subtotal + tax

  return (
    <section className="flex flex-col lg:items-end gap-3">
      <BaseTable
        plain
        className="w-full min-w-[100px] md:max-w-[400px] text-right"
      >
        <tbody>
          <tr>
            <td className="w-[1px] whitespace-nowrap">SUBTOTAL</td>
            <td>{convertNumber(subtotal).intoCurrency}</td>
          </tr>
          <tr>
            <td className="w-[1px] whitespace-nowrap">TAX</td>
            <td>{convertNumber(tax).intoCurrency}</td>
          </tr>
          <tr className="font-bold">
            <td className="w-[1px] whitespace-nowrap">TOTAL REJECT</td>
            <td>{convertNumber(total).intoCurrency}</td>
          </tr>
          <tr className="border-y-2 border-black text-lg font-bold">
            <td className="w-[1px] whitespace-nowrap">BALANCE</td>
            <td>{convertNumber(total).intoCurrency}</td>
          </tr>
        </tbody>
      </BaseTable>
    </section>
  )
}

function Approval(): JSX.Element {
  // Hooks
  const { data } = useContext(DataContext)

  return (
    <section className="flex flex-col gap-3">
      <TableSection>
        <TableSection.Row
          label="REJECT STATUS"
          value={data.reject_status}
        />
        <TableSection.Row
          label="APPROVAL STATUS"
          value={data.approve_status}
        />
        <TableSection.Row
          label="APPROVED BY"
          value={data.approve_by_name}
        />
      </TableSection>
    </section>
  )
}

function DetailBill(): JSX.Element {
  // Hooks
  const { detail } = useContext(DataContext)
  const { isDisabled } = useContext(ParamsContext)

  // Form
  const { getValues, setValue } = useFormContext<FormType>()

  return (
    <Fragment>
      {!detail.length ? (
        <tr>
          <td colSpan={12}>
            <Loading errorText="No data available" />
          </td>
        </tr>
      ) : (
        detail.map((item, key) => (
          <tr key={key}>
            <td>
              {!isDisabled && (
                <button
                  className="-rotate-90"
                  onClick={() => {
                    // Vars
                    const value = [...getValues("prorejectdetail")]
                    const product = value.find(
                      (i) => i.product_id === item.product_id
                    )

                    if (product) {
                      toast.error("Product is already on Reject Detail Table")
                      return null
                    }

                    value.push({
                      discount: item.discount,
                      price_reject: item.price_bill,
                      product_id: item.product_id,
                      quantity_bill: item.quantity_bill - 0,
                      quantity_rate: item.quantity_rate,
                      quantity_reject: 0,
                      tax_id: item.tax_id,
                      tax_rate: item.tax_rate
                    })

                    setValue("prorejectdetail", value)
                  }}>
                  <IoSendSharp />
                </button>
              )}
            </td>
            <td>{item.quantity_bill}</td>
            <td>{0}</td>
            <td>{item.quantity_bill - 0}</td>
            <td>{item.product_barcode}</td>
            <td>{item.product_name}</td>
            <td>{item.unit_measure}</td>
            <td className="text-right">{item.price_bill.toLocaleString()}</td>
            <td>{item.discount}%</td>
            <td className="text-right">{item.total.toLocaleString()}</td>
            <td>{item.tax_name}</td>
          </tr>
        ))
      )}
    </Fragment>
  )
}

function RejectDetail(): JSX.Element {
  // Form
  const { control, getValues, setValue } = useFormContext<FormType>()
  const prorejectdetail = useWatch({
    control,
    name: "prorejectdetail"
  })

  return (
    <Fragment>
      {prorejectdetail.map((item, key) => {
        return (
          <ListContext.Provider
            value={{
              item,
              index: key
            }}>
            <TableRow
              key={`${item.product_id}-${key}`}
              remove={() => {
                // Vars
                const prorejectdetail = [...getValues("prorejectdetail")]

                prorejectdetail.splice(key, 1)
                setValue("prorejectdetail", prorejectdetail)
              }}
            />
          </ListContext.Provider>
        )
      })}
    </Fragment>
  )
}
